import {
  E2EE_MIN_ITERATIONS,
  fingerprintInput,
  type E2eeKeyBackup,
  type EncryptedBody,
  type EncryptedPayload,
  type PublicJwk,
  type WrappedKey,
} from "@atrium/shared";

/*
 * End-to-end encryption primitives for direct messages. WebCrypto only, so the
 * same code runs in browsers and in Node (unit tests).
 *
 * - Identity: an ECDH P-256 key (receives conversation keys) and an ECDSA
 *   P-256 key (signs messages and key distributions), per account.
 * - Backup: both private keys sealed with AES-256-GCM under a PBKDF2-SHA256
 *   key derived from a random 160-bit recovery code, bound to the user id. A
 *   second copy is sealed the same way under the account password, so signing
 *   in unlocks this device without the code.
 * - Conversation keys: random AES-256-GCM keys, wrapped per member with an
 *   ephemeral ECDH exchange + HKDF-SHA256, signed by the member who made them.
 * - Messages: AES-256-GCM with the conversation id, author and key version as
 *   associated data, padded to hide exact length, signed by the author.
 * - Files: each attachment gets its own random AES-256-GCM key and is uploaded
 *   as ciphertext. Key, IV and the ciphertext's SHA-256 ride inside the signed
 *   message, so a swapped or altered blob is refused before it's decrypted.
 *
 * Every context string is versioned and every signature covers the context, so
 * a ciphertext or wrapped key can't be replayed into another conversation,
 * attributed to another author, or handed to another recipient.
 */

const subtle = globalThis.crypto.subtle;
const enc = new TextEncoder();
const dec = new TextDecoder();

/* ───── Encoding ───── */

export function toB64u(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromB64u(s: string): Uint8Array<ArrayBuffer> {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
  const out = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out;
}

const random = (n: number) => globalThis.crypto.getRandomValues(new Uint8Array(n));
const hex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

/* ───── Keys ───── */

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
const ECDSA = { name: "ECDSA", namedCurve: "P-256" } as const;
const SIGN = { name: "ECDSA", hash: "SHA-256" } as const;

export interface Identity {
  encryptionPrivate: CryptoKey;
  signingPrivate: CryptoKey;
  encryptionKey: PublicJwk;
  signingKey: PublicJwk;
  fingerprint: string;
}

/** Private keys as JWK, only ever held briefly while sealing or opening a backup. */
interface PrivateJwks {
  encryption: JsonWebKey;
  signing: JsonWebKey;
}

export const publicJwk = (k: JsonWebKey): PublicJwk => {
  if (k.kty !== "EC" || k.crv !== "P-256" || !k.x || !k.y) throw new Error("Not a P-256 key");
  return { kty: "EC", crv: "P-256", x: k.x, y: k.y };
};

export async function fingerprint(encryptionKey: PublicJwk, signingKey: PublicJwk): Promise<string> {
  return hex(await subtle.digest("SHA-256", enc.encode(fingerprintInput(encryptionKey, signingKey))));
}

/**
 * A 60-digit safety number two people compare out of band. Order-independent:
 * both sides see the same digits.
 */
export async function safetyNumber(fingerprintA: string, fingerprintB: string): Promise<string> {
  const [a, b] = [fingerprintA, fingerprintB].sort();
  const digest = new Uint8Array(await subtle.digest("SHA-512", enc.encode(`atrium-safety-v1|${a}|${b}`)));
  const groups: string[] = [];
  for (let i = 0; i < 12; i++) {
    const chunk = digest.slice(i * 5, i * 5 + 5).reduce((n, byte) => n * 256 + byte, 0);
    groups.push(String(chunk % 100000).padStart(5, "0"));
  }
  return groups.join(" ");
}

const importPrivate = (jwk: JsonWebKey, kind: "encryption" | "signing") =>
  // Non-extractable: page script (including an XSS payload) can use the key but never read it out.
  subtle.importKey("jwk", jwk, kind === "encryption" ? ECDH : ECDSA, false, kind === "encryption" ? ["deriveBits"] : ["sign"]);

const importPublic = (jwk: PublicJwk, kind: "encryption" | "signing") =>
  subtle.importKey("jwk", { ...jwk, ext: true }, kind === "encryption" ? ECDH : ECDSA, true, kind === "encryption" ? [] : ["verify"]);

/** A fresh identity, plus its private keys as JWK for sealing into the backup. */
export async function generateIdentity(): Promise<{ identity: Identity; privateJwks: PrivateJwks }> {
  const [e, s] = await Promise.all([subtle.generateKey(ECDH, true, ["deriveBits"]), subtle.generateKey(ECDSA, true, ["sign", "verify"])]);
  const privateJwks = { encryption: await subtle.exportKey("jwk", e.privateKey), signing: await subtle.exportKey("jwk", s.privateKey) };
  return { identity: await identityFromJwks(privateJwks), privateJwks };
}

async function identityFromJwks(jwks: PrivateJwks): Promise<Identity> {
  const encryptionKey = publicJwk(jwks.encryption);
  const signingKey = publicJwk(jwks.signing);
  const [encryptionPrivate, signingPrivate, fp] = await Promise.all([
    importPrivate(jwks.encryption, "encryption"),
    importPrivate(jwks.signing, "signing"),
    fingerprint(encryptionKey, signingKey),
  ]);
  return { encryptionPrivate, signingPrivate, encryptionKey, signingKey, fingerprint: fp };
}

/* ───── Recovery code and backup ───── */

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** 160 random bits as 32 Crockford base32 characters, in groups of four. */
export function generateRecoveryCode(): string {
  const bytes = random(20);
  let bits = 0;
  let value = 0;
  let out = "";
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return out.match(/.{4}/g)!.join("-");
}

/** Forgiving input: case, spaces and dashes don't matter; I/L read as 1 and O as 0. */
export function normaliseRecoveryCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/[IL]/g, "1")
    .replace(/O/g, "0");
}

export const isRecoveryCodeShaped = (input: string) => /^[0-9A-HJKMNP-TV-Z]{32}$/.test(normaliseRecoveryCode(input));

/** What a backup is sealed with. Each kind has its own KDF input and associated data, so one can't stand in for the other. */
type BackupSecret = { kind: "code"; code: string } | { kind: "password"; password: string };

const secretBytes = (s: BackupSecret, userId: string) =>
  enc.encode(s.kind === "code" ? normaliseRecoveryCode(s.code) : `atrium-e2ee-password-v1|${userId}|${s.password.normalize("NFKC")}`);

async function backupKey(secret: BackupSecret, userId: string, salt: Uint8Array<ArrayBuffer>, iterations: number) {
  const base = await subtle.importKey("raw", secretBytes(secret, userId), "PBKDF2", false, ["deriveKey"]);
  return subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

const backupAad = (secret: BackupSecret, userId: string) => enc.encode(`${secret.kind === "code" ? "atrium-e2ee-backup-v1" : "atrium-e2ee-password-backup-v1"}|${userId}`);

async function seal(privateJwks: PrivateJwks, secret: BackupSecret, userId: string, iterations: number): Promise<E2eeKeyBackup> {
  const salt = random(16);
  const iv = random(12);
  const key = await backupKey(secret, userId, salt, iterations);
  const body = enc.encode(JSON.stringify({ encryption: privateJwks.encryption, signing: privateJwks.signing }));
  const ct = await subtle.encrypt({ name: "AES-GCM", iv, additionalData: backupAad(secret, userId) }, key, body);
  return { v: 1, kdf: "PBKDF2-SHA256", iterations, salt: toB64u(salt), iv: toB64u(iv), ct: toB64u(ct) };
}

export const sealBackup = (privateJwks: PrivateJwks, code: string, userId: string, iterations = E2EE_MIN_ITERATIONS) => seal(privateJwks, { kind: "code", code }, userId, iterations);

/**
 * The private keys sealed under the account password. Weaker than the recovery
 * code against the server (it sees the password at sign-in), in exchange for
 * signing in being enough to read messages on a new device.
 */
export const sealPasswordBackup = (privateJwks: PrivateJwks, password: string, userId: string, iterations = E2EE_MIN_ITERATIONS) =>
  seal(privateJwks, { kind: "password", password }, userId, iterations);

export class WrongRecoveryCodeError extends Error {
  constructor() {
    super("That recovery code doesn't match");
  }
}

/**
 * Open a backup with a recovery code. `expected` (the public keys the server
 * lists for this account) must match what's inside, so a tampered backup can't
 * slip in different keys.
 */
export const openBackup = (backup: E2eeKeyBackup, code: string, userId: string, expected?: { encryptionKey: PublicJwk; signingKey: PublicJwk }) =>
  open(backup, { kind: "code", code }, userId, expected);

export class WrongPasswordError extends Error {
  constructor() {
    super("That password doesn't unlock your encrypted messages");
  }
}

/** Open the password-sealed copy. Throws WrongPasswordError if the password changed since it was sealed. */
export const openPasswordBackup = (backup: E2eeKeyBackup, password: string, userId: string, expected?: { encryptionKey: PublicJwk; signingKey: PublicJwk }) =>
  open(backup, { kind: "password", password }, userId, expected);

async function open(backup: E2eeKeyBackup, secret: BackupSecret, userId: string, expected?: { encryptionKey: PublicJwk; signingKey: PublicJwk }): Promise<{ identity: Identity; privateJwks: PrivateJwks }> {
  if (backup.v !== 1 || backup.kdf !== "PBKDF2-SHA256" || backup.iterations < E2EE_MIN_ITERATIONS) throw new Error("Unsupported key backup");
  const key = await backupKey(secret, userId, fromB64u(backup.salt), backup.iterations);
  let plain: ArrayBuffer;
  try {
    plain = await subtle.decrypt({ name: "AES-GCM", iv: fromB64u(backup.iv), additionalData: backupAad(secret, userId) }, key, fromB64u(backup.ct));
  } catch {
    throw secret.kind === "code" ? new WrongRecoveryCodeError() : new WrongPasswordError();
  }
  const privateJwks = JSON.parse(dec.decode(plain)) as PrivateJwks;
  const identity = await identityFromJwks(privateJwks);
  if (expected && (!sameKey(identity.encryptionKey, expected.encryptionKey) || !sameKey(identity.signingKey, expected.signingKey))) {
    throw new Error("The key backup doesn't match this account's keys");
  }
  return { identity, privateJwks };
}

export const sameKey = (a: PublicJwk, b: PublicJwk) => a.x === b.x && a.y === b.y && a.crv === b.crv;

/* ───── Signatures ───── */

async function sign(key: CryptoKey, data: string) {
  return toB64u(await subtle.sign(SIGN, key, enc.encode(data)));
}

async function verify(key: PublicJwk, sig: string, data: string) {
  try {
    return await subtle.verify(SIGN, await importPublic(key, "signing"), fromB64u(sig), enc.encode(data));
  } catch {
    return false;
  }
}

/* ───── Conversation keys ───── */

export interface WrapContext {
  conversationId: string;
  version: number;
  recipientId: string;
  senderId: string;
}

const wrapInfo = (c: WrapContext) => `atrium-e2ee-wrap-v1|${c.conversationId}|${c.version}|${c.recipientId}|${c.senderId}`;

/** A fresh conversation key: raw bytes (to wrap for members) and the usable key. */
export async function newConversationKey(): Promise<{ raw: Uint8Array<ArrayBuffer>; key: CryptoKey }> {
  const raw = random(32);
  return { raw, key: await subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]) };
}

async function wrapAesKey(shared: ArrayBuffer, info: string, epk: PublicJwk) {
  const hkdf = await subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const salt = await subtle.digest("SHA-256", enc.encode(`${epk.x}|${epk.y}`));
  return subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt, info: enc.encode(info) }, hkdf, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function wrapConversationKey(raw: Uint8Array<ArrayBuffer>, recipientKey: PublicJwk, ctx: WrapContext, sender: Identity): Promise<WrappedKey> {
  const eph = await subtle.generateKey(ECDH, true, ["deriveBits"]);
  const epk = publicJwk(await subtle.exportKey("jwk", eph.publicKey));
  const shared = await subtle.deriveBits({ name: "ECDH", public: await importPublic(recipientKey, "encryption") }, eph.privateKey, 256);
  const info = wrapInfo(ctx);
  const aes = await wrapAesKey(shared, info, epk);
  const iv = random(12);
  const ct = toB64u(await subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc.encode(info) }, aes, raw));
  const ivs = toB64u(iv);
  const sig = await sign(sender.signingPrivate, `${info}|${epk.x}|${epk.y}|${ivs}|${ct}`);
  return { v: 1, epk, iv: ivs, ct, sig };
}

export class UntrustedError extends Error {}

/** Unwrap a conversation key sealed for us. The sender's signature is checked before anything is decrypted. */
export async function unwrapConversationKey(w: WrappedKey, ctx: WrapContext, me: Identity, senderSigningKey: PublicJwk): Promise<CryptoKey> {
  if (w.v !== 1) throw new UntrustedError("Unsupported key format");
  const info = wrapInfo(ctx);
  if (!(await verify(senderSigningKey, w.sig, `${info}|${w.epk.x}|${w.epk.y}|${w.iv}|${w.ct}`))) throw new UntrustedError("Key signature doesn't verify");
  const shared = await subtle.deriveBits({ name: "ECDH", public: await importPublic(w.epk, "encryption") }, me.encryptionPrivate, 256);
  const aes = await wrapAesKey(shared, info, w.epk);
  const raw = await subtle.decrypt({ name: "AES-GCM", iv: fromB64u(w.iv), additionalData: enc.encode(info) }, aes, fromB64u(w.ct));
  if (raw.byteLength !== 32) throw new UntrustedError("Bad key length");
  return subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);
}

/* ───── Messages ───── */

export interface MessageContext {
  conversationId: string;
  authorId: string;
  keyVersion: number;
}

const msgInfo = (c: MessageContext) => `atrium-e2ee-msg-v1|${c.conversationId}|${c.authorId}|${c.keyVersion}`;

/** Pad to a multiple of 64 bytes (JSON tolerates trailing spaces), so ciphertext length only hints at size. */
function pad(json: string) {
  const bytes = enc.encode(json);
  const size = Math.ceil((bytes.length + 1) / 64) * 64;
  const out = new Uint8Array(size).fill(0x20);
  out.set(bytes);
  return out;
}

export async function encryptMessage(body: EncryptedBody, key: CryptoKey, ctx: MessageContext, author: Identity): Promise<EncryptedPayload> {
  const iv = random(12);
  const info = msgInfo(ctx);
  const ct = toB64u(await subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc.encode(info) }, key, pad(JSON.stringify(body))));
  const ivs = toB64u(iv);
  return { v: 1, keyVersion: ctx.keyVersion, iv: ivs, ct, sig: await sign(author.signingPrivate, `${info}|${ivs}|${ct}`) };
}

/** Decrypt and authenticate a message. Throws if the author's signature or the ciphertext don't check out. */
export async function decryptMessage(p: EncryptedPayload, key: CryptoKey, ctx: MessageContext, authorSigningKey: PublicJwk): Promise<EncryptedBody> {
  if (p.v !== 1 || p.keyVersion !== ctx.keyVersion) throw new UntrustedError("Unsupported message format");
  const info = msgInfo(ctx);
  if (!(await verify(authorSigningKey, p.sig, `${info}|${p.iv}|${p.ct}`))) throw new UntrustedError("Message signature doesn't verify");
  const plain = await subtle.decrypt({ name: "AES-GCM", iv: fromB64u(p.iv), additionalData: enc.encode(info) }, key, fromB64u(p.ct));
  const body = JSON.parse(dec.decode(plain)) as unknown;
  if (!body || typeof body !== "object" || typeof (body as EncryptedBody).content !== "string") throw new UntrustedError("Malformed message");
  return body as EncryptedBody;
}

/* ───── Files ───── */

const FILE_AAD = enc.encode("atrium-e2ee-file-v1");

/** Encrypt one file under a fresh key. The key, IV and hash go inside the (signed) message; the ciphertext is uploaded. */
export async function encryptFile(plain: BufferSource): Promise<{ data: Uint8Array<ArrayBuffer>; key: string; iv: string; sha256: string }> {
  const raw = random(32);
  const iv = random(12);
  const key = await subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt"]);
  const data = new Uint8Array(await subtle.encrypt({ name: "AES-GCM", iv, additionalData: FILE_AAD }, key, plain));
  const out = { data, key: toB64u(raw), iv: toB64u(iv), sha256: toB64u(await subtle.digest("SHA-256", data)) };
  raw.fill(0);
  return out;
}

/** Check a downloaded ciphertext against its hash, then decrypt it. Throws UntrustedError on any mismatch. */
export async function decryptFile(data: ArrayBuffer, d: { key: string; iv: string; sha256: string }): Promise<ArrayBuffer> {
  if (toB64u(await subtle.digest("SHA-256", data)) !== d.sha256) throw new UntrustedError("The file doesn't match its message");
  const raw = fromB64u(d.key);
  if (raw.byteLength !== 32) throw new UntrustedError("Bad key length");
  const key = await subtle.importKey("raw", raw, "AES-GCM", false, ["decrypt"]);
  try {
    return await subtle.decrypt({ name: "AES-GCM", iv: fromB64u(d.iv), additionalData: FILE_AAD }, key, data);
  } catch {
    throw new UntrustedError("The file couldn't be decrypted");
  }
}
